/**
 * The risk model a draft was saved with, read back onto its rows. An instrument added in the editor
 * gets its risk model from the user's fix of its check (`../new-instruments`), and the draft save
 * carries it on the row's `proxies`; the customer's instrument records learn it only once the
 * portfolio is reviewed. Until then the saved row is the only place it lives, so a row the records
 * give no risk model takes the one it was saved with — otherwise the next save would send none.
 */
import type { ReviewTicker } from "@eu.mdotm.api/sphere";
import type { Instrument } from "../../const";
import { riskModelFromProxy } from "../new-instruments/checks";

/**
 * `instruments` with the risk model each was saved with, where the customer's records give it none.
 * `instruments` is `createInstrumentList`'s answer for `composition` — one row per entry, in the same
 * order — so the two are read side by side.
 */
export function withSavedRiskModels(instruments: Instrument[], composition: ReviewTicker[]): Instrument[] {
	return instruments.map((instrument, index) => {
		const saved = composition[index];
		if (
			instrument.instrumentType !== "INSTRUMENT" ||
			(instrument.riskModel?.length ?? 0) > 0 ||
			!saved?.proxies?.length
		) {
			return instrument;
		}
		return {
			...instrument,
			riskModel: saved.proxies.map(riskModelFromProxy),
			proxyOverwriteType: saved.proxyOverwriteType ?? instrument.proxyOverwriteType,
		};
	});
}
